import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AuthResponse, TokenPayload } from '@app/core.model';
import { CurrentClinic, CurrentUser } from '@app/types';
import jwtDecode from 'jwt-decode';
import { of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { Role } from './Roles/roleEnum';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    @Inject('LOCALSTORAGE') private localStorage: Storage
  ) {}

  currentRoles: number[];

  login(email: string, password: string) {
    return this.http
      .post<AuthResponse>(`${environment.baseAPIUrl}/auth/basic/init`, {
        emailAddress: email,
        password: password,
        sourceApp: '3',
      })
      .pipe(
        map((response: AuthResponse) => {
          // set token property
          if (!response.token) {
            return false;
          }

          const tokenPayload = jwtDecode<TokenPayload>(response.token);
          // store email and jwt token in local storage to keep user logged in between page refreshes
          this.localStorage.setItem(
            'currentUser',
            JSON.stringify({
              clinic: response.user.clinic,
              token: response.token,
              isAdmin: true,
              email: email,
              id: tokenPayload.id,
              roles: tokenPayload.roles,
              expiration: new Date(tokenPayload.exp * 1000),
              fullName: response.user.name,
            })
          );

          return true;
        })
      );
  }

  logout(): void {
    // clear token remove user from local storage to log user out
    this.localStorage.removeItem('currentUser');
    this.localStorage.removeItem('currentClinic');
    location.reload();
  }

  getCurrentUser(): CurrentUser | null {
    const item = this.localStorage.getItem('currentUser');
    return item ? (JSON.parse(item) as CurrentUser) : null;
  }

  getCurrentClinic(): CurrentClinic | null {
    const item = this.localStorage.getItem('currentClinic');

    return item ? (JSON.parse(item) as CurrentClinic) : null;
  }

  passwordResetRequest(email: string) {
    return of(true).pipe(delay(1000));
  }

  changePassword(password: string) {
    return this.http.put(`${environment.baseAPIUrl}/users/me/change-password`, {
      password,
    });
  }

  passwordReset(
    email: string,
    token: string,
    password: string,
    confirmPassword: string
  ): any {
    return of(true).pipe(delay(1000));
  }

  isClinicManager() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(7)) {
      return true;
    }
    return false;
  }

  isAdmin() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(3)) {
      return true;
    }
    return false;
  }

  isOpsManager() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(8)) {
      return true;
    }
    return false;
  }

  isReceptionist() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(4)) {
      return true;
    }
    return false;
  }

  isWhatsAppOperator() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(9)) {
      return true;
    }
    return false;
  }

  isPharmacyOperator() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(10)) {
      return true;
    }
    return false;
  }

  isPharmacyManager() {
    const roles = this.getCurrentUser()?.roles;

    if (roles?.includes(12)) {
      return true;
    }
    return false;
  }

  isStockTransfer() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(18)) {
      return true;
    }
    return false;
  }

  /** Pharmacy Manager–level pharmacy module access (includes Stock Transfer). */
  hasPharmacyManagerAccess() {
    return this.isPharmacyManager() || this.isStockTransfer();
  }

  canBypassPharmacyOpdMarginRestriction(): boolean {
    return this.isAdmin() || this.isMaximumDiscountOverride();
  }

  isPharmacist() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(11)) {
      return true;
    }
    return false;
  }

  isAuditor() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(13)) {
      return true;
    }
    return false;
  }

  isTaskReviewer() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(14)) {
      return true;
    }
    return false;
  }

  isStoreManager() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(15)) {
      return true;
    }
    return false;
  }

  isStoreApproval() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(17)) {
      return true;
    }
    return false;
  }

  isBackdateBillCreator() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(Role.BackdateBillCreator)) {
      return true;
    }
    return false;
  }

  isMaximumDiscountOverride() {
    const roles = this.getCurrentUser()?.roles;
    if (roles?.includes(Role.MaximumDiscountOverride)) {
      return true;
    }
    return false;
  }
}
